// YouTube Data API Service for El Bethel Ministries

const YOUTUBE_API_KEY = process.env.REACT_APP_YOUTUBE_API_KEY;
const BASE_URL = 'https://www.googleapis.com/youtube/v3';

class YouTubeAPI {
  constructor() {
    this.apiKey = YOUTUBE_API_KEY;
    this.channelId = null;
    this.cacheKey = 'youtube_videos_cache';
    this.cacheExpiry = 'youtube_cache_expiry';
    this.cacheTimeout = 30 * 60 * 1000; // 30 minutes
  }

  // Clear cache and force refresh
  clearCache() {
    localStorage.removeItem(this.cacheKey);
    localStorage.removeItem(this.cacheExpiry);
  }

  // Check if cache is valid
  isCacheValid() {
    const expiry = localStorage.getItem(this.cacheExpiry);
    if (!expiry) return false;
    
    return new Date().getTime() < parseInt(expiry);
  }

  // Get cached videos
  getCachedVideos() {
    if (!this.isCacheValid()) return null;
    
    const cached = localStorage.getItem(this.cacheKey);
    return cached ? JSON.parse(cached) : null;
  }

  // Cache videos
  setCacheVideos(videos) {
    localStorage.setItem(this.cacheKey, JSON.stringify(videos));
    localStorage.setItem(this.cacheExpiry, (new Date().getTime() + this.cacheTimeout).toString());
  }

  // Get channel ID from channel handle
  async getChannelId(channelHandle = '@ElBethelGlobal') {
    try {
      if (this.channelId) return this.channelId;

      const response = await fetch(
        `${BASE_URL}/channels?part=id&forHandle=${channelHandle}&key=${this.apiKey}`
      );
      
      if (!response.ok) {
        throw new Error(`YouTube API error: ${response.status}`);
      }

      const data = await response.json();
      if (data.items && data.items.length > 0) {
        this.channelId = data.items[0].id;
        return this.channelId;
      }
      
      throw new Error('Channel not found');
    } catch (error) {
      console.error('Error getting channel ID:', error);
      throw error;
    }
  }

  // Get uploads playlist ID
  async getUploadsPlaylistId() {
    try {
      const channelId = await this.getChannelId();
      
      const response = await fetch(
        `${BASE_URL}/channels?part=contentDetails&id=${channelId}&key=${this.apiKey}`
      );
      
      const data = await response.json();
      return data.items[0].contentDetails.relatedPlaylists.uploads;
    } catch (error) {
      console.error('Error getting uploads playlist:', error);
      throw error;
    }
  }

  // Get all channel videos with pagination and caching
  async getAllChannelVideos(forceRefresh = false) {
    try {
      // Check cache first unless force refresh
      if (!forceRefresh) {
        const cachedVideos = this.getCachedVideos();
        if (cachedVideos) {
          console.log('Using cached YouTube videos');
          return cachedVideos;
        }
      }

      console.log('Fetching fresh YouTube videos...');
      
      const playlistId = await this.getUploadsPlaylistId();
      let allVideos = [];
      let nextPageToken = '';

      do {
        const response = await fetch(
          `${BASE_URL}/playlistItems?part=snippet&playlistId=${playlistId}&maxResults=50&pageToken=${nextPageToken}&key=${this.apiKey}`
        );
        
        const data = await response.json();
        allVideos.push(...data.items);
        nextPageToken = data.nextPageToken;
      } while (nextPageToken);

      console.log(`Fetched ${allVideos.length} videos from YouTube`);

      // Get detailed video metadata
      const videoIds = allVideos.map(video => video.snippet.resourceId.videoId);
      const videosWithMetadata = await this.getVideoMetadata(videoIds);
      
      // Categorize videos
      const categorizedVideos = this.categorizeVideos(videosWithMetadata);
      
      // Cache the results
      this.setCacheVideos(categorizedVideos);
      
      return categorizedVideos;
    } catch (error) {
      console.error('Error fetching all videos:', error);
      
      // Return cached videos if available, even if expired
      const cachedVideos = this.getCachedVideos();
      if (cachedVideos) {
        console.log('Using stale cached videos due to API error');
        return cachedVideos;
      }
      
      throw error;
    }
  }

  // Get video metadata (views, likes, duration, etc.)
  async getVideoMetadata(videoIds) {
    try {
      const chunks = [];
      // YouTube API allows max 50 IDs per request
      for (let i = 0; i < videoIds.length; i += 50) {
        chunks.push(videoIds.slice(i, i + 50));
      }

      let allVideoData = [];
      
      for (const chunk of chunks) {
        const response = await fetch(
          `${BASE_URL}/videos?part=snippet,statistics,contentDetails&id=${chunk.join(',')}&key=${this.apiKey}`
        );
        
        const data = await response.json();
        allVideoData.push(...data.items);
      }

      return allVideoData;
    } catch (error) {
      console.error('Error getting video metadata:', error);
      throw error;
    }
  }

  // Categorize videos based on title and duration
  categorizeVideos(videos) {
    const categorized = {
      sermons: [],
      shorts: [],
      bible_studies: []
    };

    videos.forEach(video => {
      const title = video.snippet.title.toLowerCase();
      const duration = this.parseDuration(video.contentDetails.duration);
      
      // YouTube Shorts are typically under 60 seconds
      if (duration <= 60) {
        categorized.shorts.push(this.formatVideoData(video));
      } else if (title.includes('bible study') || title.includes('study guide') || title.includes('scripture study')) {
        categorized.bible_studies.push(this.formatVideoData(video));
      } else {
        // All longer videos (over 60 seconds) that aren't Bible studies are sermons
        categorized.sermons.push(this.formatVideoData(video));
      }
    });

    console.log('Video categorization:', {
      sermons: categorized.sermons.length,
      shorts: categorized.shorts.length,
      bible_studies: categorized.bible_studies.length
    });

    return categorized;
  }

  // Parse ISO 8601 duration to seconds
  parseDuration(duration) {
    const match = duration.match(/PT(\d+H)?(\d+M)?(\d+S)?/);
    const hours = (parseInt(match[1]) || 0);
    const minutes = (parseInt(match[2]) || 0);
    const seconds = (parseInt(match[3]) || 0);
    return hours * 3600 + minutes * 60 + seconds;
  }

  // Format video data for frontend use
  formatVideoData(video) {
    const duration = this.parseDuration(video.contentDetails.duration);
    const publishDate = new Date(video.snippet.publishedAt);
    
    return {
      id: video.id,
      title: video.snippet.title,
      description: video.snippet.description,
      thumbnail: video.snippet.thumbnails.maxres?.url || 
                video.snippet.thumbnails.high?.url || 
                video.snippet.thumbnails.medium?.url,
      publishedAt: video.snippet.publishedAt,
      publishDate: publishDate.toLocaleDateString(),
      duration: this.formatDuration(duration),
      durationSeconds: duration,
      viewCount: parseInt(video.statistics.viewCount || 0),
      likeCount: parseInt(video.statistics.likeCount || 0),
      commentCount: parseInt(video.statistics.commentCount || 0),
      tags: video.snippet.tags || [],
      channelTitle: video.snippet.channelTitle,
      embedUrl: `https://www.youtube.com/embed/${video.id}`,
      watchUrl: `https://www.youtube.com/watch?v=${video.id}`
    };
  }

  // Format duration for display
  formatDuration(seconds) {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    } else {
      return `${minutes}:${secs.toString().padStart(2, '0')}`;
    }
  }

  // Search videos
  async searchVideos(query, category = 'all') {
    try {
      const allVideos = await this.getAllChannelVideos();
      let videosToSearch = [];
      
      if (category === 'all') {
        videosToSearch = [...allVideos.sermons, ...allVideos.shorts, ...allVideos.bible_studies];
      } else {
        videosToSearch = allVideos[category] || [];
      }

      if (!query) return videosToSearch;

      return videosToSearch.filter(video =>
        video.title.toLowerCase().includes(query.toLowerCase()) ||
        video.description.toLowerCase().includes(query.toLowerCase()) ||
        video.tags.some(tag => tag.toLowerCase().includes(query.toLowerCase()))
      );
    } catch (error) {
      console.error('Error searching videos:', error);
      throw error;
    }
  }
}

// Export singleton instance
export default new YouTubeAPI();